import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CertificationProgress = ({ className = '' }) => {
  const [currentLevel, setCurrentLevel] = useState('bronze');
  
  const certificationData = {
    currentLevel: 'bronze',
    nextLevel: 'silver',
    progress: 68,
    totalPoints: 850,
    requiredPoints: 1000,
    verificationStatus: 'pending'
  };

  const certificationLevels = [
    {
      id: 'bronze',
      name: 'Bronce',
      color: 'var(--color-warning)',
      bgColor: 'bg-warning/10',
      borderColor: 'border-warning/20',
      requirements: 500,
      benefits: ['Insignia básica', 'Listado estándar', 'Soporte básico']
    },
    {
      id: 'silver',
      name: 'Plata',
      color: 'var(--color-muted-foreground)',
      bgColor: 'bg-muted/20',
      borderColor: 'border-muted',
      requirements: 1000,
      benefits: ['Posición destacada', 'Análisis avanzado', 'Soporte prioritario']
    },
    {
      id: 'gold',
      name: 'Oro',
      color: 'var(--color-accent)',
      bgColor: 'bg-accent/10',
      borderColor: 'border-accent/20',
      requirements: 2000,
      benefits: ['Posición premium', 'Herramientas exclusivas', 'Soporte dedicado']
    }
  ];

  const verificationSteps = [
    {
      id: 'data',
      title: 'Datos de Plataforma',
      description: 'Análisis automático de tu actividad',
      status: 'completed',
      points: 300,
      icon: 'BarChart3'
    },
    {
      id: 'practices',
      title: 'Lista de Prácticas',
      description: 'Cuestionario de sostenibilidad',
      status: 'completed',
      points: 250,
      icon: 'CheckSquare'
    },
    {
      id: 'inspection',
      title: 'Verificación NGO',
      description: 'Inspección presencial pendiente',
      status: 'pending',
      points: 300,
      icon: 'Shield'
    },
    {
      id: 'documentation',
      title: 'Documentación',
      description: 'Certificados y permisos',
      status: 'in-progress',
      points: 150,
      icon: 'FileText'
    }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return 'CheckCircle';
      case 'in-progress':
        return 'Clock';
      case 'pending':
        return 'AlertCircle';
      default:
        return 'Circle';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-success';
      case 'in-progress':
        return 'text-warning';
      case 'pending':
        return 'text-muted-foreground';
      default:
        return 'text-muted-foreground';
    }
  };

  const completedSteps = verificationSteps?.filter(step => step?.status === 'completed');
  const totalEarnedPoints = completedSteps?.reduce((sum, step) => sum + step?.points, 0);
  const progressPercentage = (totalEarnedPoints / certificationData?.requiredPoints) * 100;

  const currentLevelData = certificationLevels?.find(level => level?.id === certificationData?.currentLevel);
  const nextLevelData = certificationLevels?.find(level => level?.id === certificationData?.nextLevel);

  return (
    <div className={`bg-card border border-border rounded-lg p-6 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-heading font-semibold text-foreground">
            Certificación Zero Waste
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Progreso hacia la certificación Plata
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          <div className={`px-3 py-1 rounded-full ${currentLevelData?.bgColor} ${currentLevelData?.borderColor} border`}>
            <span className="text-sm font-medium" style={{ color: currentLevelData?.color }}>
              Nivel {currentLevelData?.name}
            </span>
          </div>
        </div>
      </div>
      {/* Progress Overview */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">
            Progreso hacia Plata
          </span>
          <span className="text-sm text-muted-foreground">
            {totalEarnedPoints} / {certificationData?.requiredPoints} puntos
          </span>
        </div>
        
        <div className="w-full bg-muted rounded-full h-3">
          <div 
            className="bg-primary rounded-full h-3 transition-all duration-500"
            style={{ width: `${Math.min(progressPercentage, 100)}%` }}
          />
        </div>
        
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-muted-foreground">
            {Math.round(progressPercentage)}% completado
          </span>
          <span className="text-xs text-muted-foreground">
            {certificationData?.requiredPoints - totalEarnedPoints} puntos restantes
          </span>
        </div>
      </div>
      {/* Certification Levels */}
      <div className="mb-6">
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
          Niveles de Certificación
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {certificationLevels?.map((level) => {
            const isCurrentLevel = level?.id === certificationData?.currentLevel;
            const isNextLevel = level?.id === certificationData?.nextLevel;
            const isUnlocked = totalEarnedPoints >= level?.requirements;
            
            return (
              <div
                key={level?.id}
                className={`p-4 rounded-lg border transition-all ${
                  isCurrentLevel 
                    ? `${level?.bgColor} ${level?.borderColor} border-2` 
                    : isUnlocked 
                      ? 'bg-success/5 border-success/20' :'bg-muted/30 border-border'
                }`}
              >
                <div className="flex items-center space-x-2 mb-3">
                  <Icon 
                    name={isUnlocked ? 'Award' : 'Lock'} 
                    size={20} 
                    color={isUnlocked ? level?.color : 'var(--color-muted-foreground)'} 
                  />
                  <span 
                    className="font-medium"
                    style={{ color: isUnlocked ? level?.color : 'var(--color-muted-foreground)' }}
                  >
                    {level?.name}
                  </span>
                  {isCurrentLevel && (
                    <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                      Actual
                    </span>
                  )}
                  {isNextLevel && (
                    <span className="text-xs bg-accent text-accent-foreground px-2 py-0.5 rounded-full">
                      Siguiente
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                  {level?.requirements} puntos requeridos
                </p>
                <ul className="space-y-1">
                  {level?.benefits?.map((benefit, index) => (
                    <li key={index} className="text-xs text-muted-foreground flex items-center">
                      <Icon name="Check" size={12} className="mr-1 flex-shrink-0" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
      {/* Verification Steps */}
      <div className="mb-6">
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
          Pasos de Verificación
        </h3>
        
        <div className="space-y-4">
          {verificationSteps?.map((step, index) => (
            <div key={step?.id} className="flex items-center space-x-4 p-4 bg-muted/30 rounded-lg">
              <div className="flex-shrink-0">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  step?.status === 'completed' 
                    ? 'bg-success text-success-foreground' 
                    : step?.status === 'in-progress' ?'bg-warning text-warning-foreground' :'bg-muted text-muted-foreground'
                }`}>
                  <Icon name={getStatusIcon(step?.status)} size={20} />
                </div>
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-foreground">
                    {step?.title}
                  </h4>
                  <span className="text-sm font-medium text-foreground">
                    +{step?.points} pts
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {step?.description}
                </p>
              </div>
              
              <div className="flex-shrink-0">
                {step?.status === 'pending' && (
                  <Button variant="outline" size="sm">
                    Iniciar
                  </Button>
                )}
                {step?.status === 'in-progress' && (
                  <Button variant="default" size="sm">
                    Continuar
                  </Button>
                )}
                {step?.status === 'completed' && (
                  <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Next Steps */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Lightbulb" size={20} color="var(--color-primary)" />
          <div className="flex-1">
            <h4 className="font-medium text-primary mb-2">
              Próximos Pasos Recomendados
            </h4>
            <ul className="space-y-1 text-sm text-primary/80">
              <li>• Completa la documentación pendiente (+150 pts)</li>
              <li>• Programa la verificación NGO (+300 pts)</li>
              <li>• Mantén tu actividad constante para puntos adicionales</li>
            </ul>
            <Button variant="default" size="sm" className="mt-3">
              <Icon name="ArrowRight" size={14} className="mr-2" />
              Ver Guía Completa
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificationProgress;